import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { getAuditLogs } from '../../services/api';
import { formatDateTime } from '../../utils/formatDateTime';

const A_BADGE = {
  CREATE:'badge-success', UPDATE:'badge-info', DELETE:'badge-danger',
  LOGIN:'badge-gray', LOGIN_FAILED:'badge-danger', IMPERSONATE:'badge-warning',
  CONFIRM_PAYMENT:'badge-success', CANCEL_INVOICE:'badge-danger',
  CHANGE_PASSWORD:'badge-info', CONNECT_GOOGLE_CALENDAR:'badge-info',
  DISCONNECT_GOOGLE_CALENDAR:'badge-gray', ENABLE_INTEGRATION:'badge-success',
  DISABLE_INTEGRATION:'badge-gray',
};

// Tradução completa de TODAS as actions do sistema pra português.
// Se uma action nova não estiver aqui, mostra o nome original (fallback).
const A_LABEL = {
  // CRUD genérico
  CREATE:'Criação', UPDATE:'Atualização', DELETE:'Exclusão',
  // Auth
  LOGIN:'Login', LOGIN_FAILED:'Tentativa de login', IMPERSONATE:'Acesso como cliente',
  CHANGE_PASSWORD:'Senha alterada',
  // Agendamentos
  CREATE_APPOINTMENT:'Agendamento criado', UPDATE_APPOINTMENT:'Agendamento atualizado',
  DELETE_APPOINTMENT:'Agendamento excluído', CANCEL_APPOINTMENT:'Agendamento cancelado',
  // Usuários
  CREATE_USER:'Usuário criado', UPDATE_USER:'Usuário atualizado', DELETE_USER:'Usuário excluído',
  // Faturamento
  CONFIRM_PAYMENT:'Pagamento confirmado', CANCEL_INVOICE:'Fatura cancelada',
  CREATE_INVOICE:'Fatura criada',
  // Integrações
  CONNECT_GOOGLE_CALENDAR:'Google Calendar conectado',
  DISCONNECT_GOOGLE_CALENDAR:'Google Calendar desconectado',
  ENABLE_INTEGRATION:'Integração ativada', DISABLE_INTEGRATION:'Integração desativada',
  // Enterprise
  CREATE_API_TOKEN:'Token API criado', REVOKE_API_TOKEN:'Token API revogado',
  CREATE_CAMPAIGN:'Campanha criada', UPDATE_CAMPAIGN:'Campanha atualizada', DELETE_CAMPAIGN:'Campanha excluída',
  // Tenant
  CREATE_TENANT:'Empresa criada', UPDATE_TENANT:'Empresa atualizada', DELETE_TENANT:'Empresa excluída',
  EXPORT_TENANT_DATA:'Dados exportados',
  // Notificações
  CREATE_NOTIFICATION:'Notificação gerada', READ_NOTIFICATION:'Notificação lida',
  // Webhooks
  CREATE_WEBHOOK:'Webhook criado', UPDATE_WEBHOOK:'Webhook atualizado', DELETE_WEBHOOK:'Webhook excluído',
  // Multi-unidade
  CREATE_UNIT:'Unidade criada', UPDATE_UNIT:'Unidade atualizada', DELETE_UNIT:'Unidade excluída',
  // Clientes
  CREATE_CLIENT:'Cliente cadastrado', UPDATE_CLIENT:'Cliente atualizado', DELETE_CLIENT:'Cliente excluído',
  // Serviços
  CREATE_SERVICE:'Serviço criado', UPDATE_SERVICE:'Serviço atualizado', DELETE_SERVICE:'Serviço excluído',
  // Roles
  CREATE_ROLE:'Perfil criado', UPDATE_ROLE:'Perfil atualizado', DELETE_ROLE:'Perfil excluído',
};

// Tradução de entity types pra português
const ENTITY_LABEL = {
  appointment: 'Agendamento',
  user: 'Usuário',
  tenant: 'Empresa',
  notification: 'Notificação',
  invoice: 'Fatura',
  service: 'Serviço',
  client: 'Cliente',
  api_token: 'Token API',
  webhook_config: 'Webhook',
  campaign: 'Campanha',
  tenant_unit: 'Unidade',
  role: 'Perfil',
};

const PAGE_SIZE = 100;

export default function TenantAudit() {
  const [logs,    setLogs]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [offset,  setOffset]  = useState(0);
  const [loading, setLoading] = useState(true);
  const [detail,  setDetail]  = useState(null);
  const [filters, setFilters] = useState({ entity:'', action:'', date_from:'', date_to:'' });

  const load = useCallback((off = 0) => {
    setLoading(true);
    getAuditLogs({ ...filters, limit: PAGE_SIZE, offset: off })
      .then(r => { setLogs(r.data.audit_logs); setTotal(r.data.total ?? r.data.audit_logs.length); setOffset(off); })
      .catch(() => toast.error('Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [filters]);
  useEffect(() => { load(0); }, [load]);

  const setF = (k, v) => setFilters(f => ({...f, [k]: v}));
  const entities = [...new Set(logs.map(l => l.entity))].sort();
  const hasFilters = Object.values(filters).some(Boolean);
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const prettyJson = v => {
    if (!v) return 'N/A';
    try { return JSON.stringify(typeof v==='string' ? JSON.parse(v) : v, null, 2); }
    catch { return String(v); }
  };

  return (
    <div className="page-body">
      <div className="page-header">
        <h2>Auditoria</h2>
        <p>Rastreabilidade completa de todas as ações do sistema</p>
      </div>

      <div className="filter-bar">
        <select value={filters.entity} onChange={e => setF('entity', e.target.value)}>
          <option value="">Todas as entidades</option>
          {entities.map(e => <option key={e} value={e}>{ENTITY_LABEL[e] || e}</option>)}
        </select>
        <select value={filters.action} onChange={e => setF('action', e.target.value)}>
          <option value="">Todas as ações</option>
          {Object.entries(A_LABEL).map(([key, label]) =>
            <option key={key} value={key}>{label}</option>
          )}
        </select>
        <div style={{ display:'flex', flexDirection:'column', gap:2, flex:1, minWidth:140 }}>
          <label style={{ fontSize:11, fontWeight:600, color:'var(--gray-500)', paddingLeft:2 }}>📅 De</label>
          <div className="date-input-wrap"><input type="date" value={filters.date_from} onChange={e => setF('date_from', e.target.value)} style={{ maxWidth:'100%' }} /></div>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:2, flex:1, minWidth:140 }}>
          <label style={{ fontSize:11, fontWeight:600, color:'var(--gray-500)', paddingLeft:2 }}>📅 Até</label>
          <div className="date-input-wrap"><input type="date" value={filters.date_to} onChange={e => setF('date_to', e.target.value)} style={{ maxWidth:'100%' }} /></div>
        </div>
        {hasFilters && <button className="btn btn-ghost btn-sm" onClick={() => setFilters({entity:'',action:'',date_from:'',date_to:''})}>✕ Limpar</button>}
      </div>

      {loading ? <div className="spinner" style={{margin:'40px auto',display:'block'}} /> : (
        logs.length === 0
          ? <div className="empty-state"><div className="empty-icon">🔍</div><h3>Nenhum log encontrado</h3></div>
          : <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {logs.map(log => (
                <div key={log.id} className="mobile-card" style={{ padding:'12px 16px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:8 }}>
                    <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                      <span className={`badge ${A_BADGE[log.action] || 'badge-gray'}`} style={{ fontSize:11 }}>{A_LABEL[log.action] || log.action}</span>
                      <code style={{ fontSize:11, background:'var(--gray-100)', padding:'2px 6px', borderRadius:4 }}>{ENTITY_LABEL[log.entity] || log.entity}{log.entity_id ? ` #${log.entity_id}` : ''}</code>
                    </div>
                    <div style={{ fontSize:11, color:'var(--gray-400)', whiteSpace:'nowrap' }}>
                      {formatDateTime(log.created_at, { year: undefined })}
                    </div>
                  </div>
                  <div style={{ marginTop:8, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div>
                      <div style={{ fontWeight:600, fontSize:13 }}>{log.user_name || 'Sistema'}</div>
                      <div style={{ fontSize:11, color:'var(--gray-400)' }}>{log.user_email || log.ip_address || ''}</div>
                    </div>
                    <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                      <span className={`badge ${log.status === 'failure' ? 'badge-danger' : 'badge-success'}`} style={{ fontSize:10 }}>
                        {log.status === 'failure' ? '✕' : '✓'}
                      </span>
                      {(log.before_data || log.after_data) && (
                        <button className="btn btn-xs btn-secondary" onClick={() => setDetail(log)}>Ver</button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:12, marginTop:16 }}>
          <button className="btn btn-secondary btn-sm" disabled={offset === 0 || loading} onClick={() => load(offset - PAGE_SIZE)}>
            ← Anterior
          </button>
          <span style={{ fontSize:13, color:'var(--gray-600)' }}>
            Página {currentPage} de {totalPages} — {total} registros
          </span>
          <button className="btn btn-secondary btn-sm" disabled={offset + PAGE_SIZE >= total || loading} onClick={() => load(offset + PAGE_SIZE)}>
            Próxima →
          </button>
        </div>
      )}

      {detail && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setDetail(null)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <h3>Log #{detail.id} — {detail.action} · {detail.entity}</h3>
              <button className="modal-close" onClick={() => setDetail(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))',gap:16}}>
                <div>
                  <div style={{fontSize:12,fontWeight:700,marginBottom:6,color:'var(--gray-600)'}}>ANTES</div>
                  <pre style={{background:'var(--gray-50)',border:'1px solid var(--gray-200)',borderRadius:8,padding:12,fontSize:11,overflow:'auto',maxHeight:260,lineHeight:1.6}}>
                    {prettyJson(detail.before_data)}
                  </pre>
                </div>
                <div>
                  <div style={{fontSize:12,fontWeight:700,marginBottom:6,color:'var(--gray-600)'}}>DEPOIS</div>
                  <pre style={{background:'var(--gray-50)',border:'1px solid var(--gray-200)',borderRadius:8,padding:12,fontSize:11,overflow:'auto',maxHeight:260,lineHeight:1.6}}>
                    {prettyJson(detail.after_data)}
                  </pre>
                </div>
              </div>
              <div style={{marginTop:16,display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:10}}>
                {[['Usuário',detail.user_name||'—'],['Ação',detail.action],['Entidade',`${detail.entity}#${detail.entity_id||'—'}`],
                  ['Tenant',detail.tenant_name||'—'],['IP',detail.ip_address||'—'],['Data',formatDateTime(detail.created_at)],
                ].map(([k,v]) => (
                  <div key={k} style={{background:'var(--gray-50)',borderRadius:8,padding:'8px 12px'}}>
                    <div style={{fontSize:10,color:'var(--gray-400)',fontWeight:700,textTransform:'uppercase',letterSpacing:'.5px'}}>{k}</div>
                    <div style={{fontSize:12,fontWeight:600,marginTop:2}}>{v}</div>
                  </div>
                ))}
              </div>
              {detail.error_message && (
                <div className="badge-danger" style={{marginTop:12,borderRadius:8,padding:'10px 14px',fontSize:12}}>
                  <strong>Erro:</strong> {detail.error_message}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDetail(null)}>Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
